import classNames from 'classnames';
import {useEffect, useState} from 'react';
import {
  CalculateTwitchExtensionConnectActionResponse,
  JsonRpcException,
  usePulsoidClient,
} from '../../PulsoidClient';
import {ButtonV2} from '../../system/ButtonV2/ButtonV2';
import {Loader} from '../../system/Loader/Loader';
import css from './TwitchExtensionConfiguration.module.scss';

const CalculateActionFailedReasons = ['TwitchExtensionTokenInvalid'] as const;

type CalculateActionFailedReason = typeof CalculateActionFailedReasons[number];

export const TwitchExtensionConnectComponent = () => {
  const jwtToken = new URLSearchParams(window.location.search).get('jwtToken');
  const client = usePulsoidClient();
  const [action, setAction] =
    useState<CalculateTwitchExtensionConnectActionResponse | null>(null);

  const [failedToCalculateAction, setFailedToCalculateAction] = useState<
    CalculateActionFailedReason | 'Unknown' | null
  >(null);

  useEffect(() => {
    async function fetchData(jwtToken: string) {
      try {
        let response = await client.calculateTwitchExtensionConnectAction(
          jwtToken
        );
        setAction(response);
      } catch (error) {
        if (error instanceof JsonRpcException) {
          let reason = error.data.reason as string;
          if (CalculateActionFailedReasons.find((r) => r == reason)) {
            //@ts-ignore we know fo sure
            setFailedToCalculateAction(reason);
          } else {
            setFailedToCalculateAction('Unknown');
          }
        }
        return;
      }
    }

    if (jwtToken) {
      fetchData(jwtToken);
    }
  }, [jwtToken]);

  return (
    <div className={css.root}>
      <h1 className={css.header}>Twitch Extension Connect Helper</h1>
      <div>
        {jwtToken && failedToCalculateAction == null && (
          <div className={css.content}>
            <Loader
              description="Loading"
              variant="white"
              loading={action == null}
            />
            {action?.action == 'RewireTwitchChannelAndCreateConfiguration' && (
              <RewireTwitchChannel
                email={action?.email}
                channelName={action.channelName}
                twitchProfileEmail={action.twitchProfileEmail}
                jwtToken={jwtToken}
              />
            )}
            {action?.action == 'ConnectTwitchAccountAndCreateConfiguration' && (
              <ConnectTwitchChannel
                jwtToken={jwtToken}
                channelName={action.channelName}
              />
            )}
            {action?.action == 'AlreadyConnected' && <AlreadyConnected />}
          </div>
        )}
        {(jwtToken == null ||
          failedToCalculateAction == 'TwitchExtensionTokenInvalid') && (
          <div className={css.content}>
            To initiate Twitch Extension connection to the Pulsoid take
            following steps:
            <GoToExtensionConfigurationComponent />
          </div>
        )}
        {failedToCalculateAction == 'Unknown' && (
          <div className={css.content}>
            Something went wrong on our side, please reinitiate the process:{' '}
          </div>
        )}
      </div>
    </div>
  );
};

const GoToExtensionConfigurationComponent = () => {
  return (
    <ol>
      <li>
        go to{' '}
        <a href="https://dashboard.twitch.tv/extensions" target={'_blank'}>
          twitch dashboard
        </a>
      </li>
      <li>open 'My Extensions' tab</li>
      <li>open 'Pulsoid Heart Rate Widget' extension configuration</li>
      <li>initiate connection process</li>
    </ol>
  );
};

type StepState = 'NotStarted' | 'InProgress' | 'Succeeded' | 'Failed';

const badgeStyle = (state: StepState) => {
  return classNames({
    [css.badge_not_connected]: state == 'InProgress',
    [css.badge_connected]: state == 'Succeeded',
    [css.badge_failed]: state == 'Failed',
    [css.badge_not_started]: state == 'NotStarted',
  });
};

const RewiringStepBadgeText: Record<StepState, string> = {
  Failed: 'rewiring failed',
  Succeeded: 'rewired',
  InProgress: 'rewiring...',
  NotStarted: 'not strated',
};

const CreateConfigurationBadgeText: Record<StepState, string> = {
  Failed: 'failed',
  Succeeded: 'created',
  InProgress: 'creating...',
  NotStarted: 'not strated',
};

type RewiringFailedReason =
  | 'TwitchExtensionTokenInvalid'
  | 'BroadcasterNotFound';

const RewireTwitchChannel = ({
  channelName,
  email,
  twitchProfileEmail,
  jwtToken,
}) => {
  const [rewiringStarted, setRewiringStarted] = useState(false);
  const [rewiringState, setRewiringState] = useState<StepState>('NotStarted');
  const [rewiringFailedReason, setRewiringFailedReason] =
    useState<RewiringFailedReason | null>(null);
  const [createConfigurationState, setCreateConfigurationState] =
    useState<StepState>('NotStarted');
  const client = usePulsoidClient();

  return (
    <div>
      <div>
        <p>
          You are trying to connect the Twitch extension installed on channel{' '}
          <strong>{channelName}</strong> to the Pulsoid profile associated with
          the email <strong>{email}</strong>.
        </p>
        <p>
          However, the Twitch channel <strong>{channelName}</strong> is already
          connected to the Pulsoid profile associated with the email{' '}
          <strong>{twitchProfileEmail}</strong>.
        </p>
        <p>
          Do you wish to connect the Twitch channel{' '}
          <strong>{channelName}</strong> from the Pulsoid profile with the email{' '}
          <strong>{twitchProfileEmail}</strong> and connect it to the current
          Pulsoid profile with the email <strong>{email}</strong>?
        </p>
      </div>
      <div>
        <div className={css.step}>
          <div>Rewiring Twitch Account</div>
          <div>
            <div className={badgeStyle(rewiringState)}>
              {RewiringStepBadgeText[rewiringState]}
            </div>
          </div>
          <div>
            {rewiringFailedReason == 'TwitchExtensionTokenInvalid' && (
              <>
                Rewiring failed due to extension token being invalid. To fix
                that:
                <GoToExtensionConfigurationComponent />
              </>
            )}
            {rewiringFailedReason == 'BroadcasterNotFound' && (
              <>
                Twitch channel with <i>{channelName}</i> name is not found.
                Please <a href="https://pulsoid.net/contact">contact us</a>.
              </>
            )}
          </div>
        </div>
        <div className={css.step}>
          <div>Connecting Twitch Extension</div>
          <div>
            <div className={badgeStyle(createConfigurationState)}>
              {CreateConfigurationBadgeText[createConfigurationState]}
            </div>
          </div>
          <div>
            {createConfigurationState == 'Failed' && (
              <>
                Connecting extension failed,
                <a href="https://pulsoid.net/contact">contact us</a>.
              </>
            )}
          </div>
        </div>
      </div>
      {rewiringState == 'NotStarted' && (
        <ButtonV2
          disabled={rewiringStarted}
          withClickLoader
          onClickAsync={async () => {
            try {
              setRewiringFailedReason(null);
              setRewiringStarted(true);
              setRewiringState('InProgress');
              try {
                await client.connectTwitchAccountByTwitchExtensionToken(
                  jwtToken
                );
                setRewiringState('Succeeded');
              } catch (error) {
                if (error instanceof JsonRpcException) {
                  let reason = error.data.reason as RewiringFailedReason;
                  if (reason == 'TwitchExtensionTokenInvalid') {
                    setRewiringFailedReason('TwitchExtensionTokenInvalid');
                    setRewiringState('Failed');
                  } else if (reason == 'BroadcasterNotFound') {
                    setRewiringFailedReason('BroadcasterNotFound');
                    setRewiringState('Failed');
                  } else {
                  }
                }
                return;
              }

              setCreateConfigurationState('InProgress');
              try {
                await client.createOrGetTwitchExtensionConfiguration();
                setCreateConfigurationState('Succeeded');
              } catch {
                setCreateConfigurationState('Failed');
              }
            } finally {
              setRewiringStarted(false);
            }
          }}
        >
          Rewire Twitch Channel
        </ButtonV2>
      )}
      {rewiringState == 'Succeeded' && createConfigurationState == 'Succeeded' && (
        <div>
          <div>Twitch Extension is successfuly connected.</div>
          <a href="https://pulsoid.net/ui/twitch-extension-configuration">
            Go to Twitch Extension Configuration
          </a>
        </div>
      )}
    </div>
  );
};

const ConnectingStepBadgeText: Record<StepState, string> = {
  Failed: 'connecting failed',
  Succeeded: 'connected',
  InProgress: 'connecting...',
  NotStarted: 'not strated',
};

const ConnectTwitchChannel = ({channelName, jwtToken}) => {
  const [connectingStarted, setConnectingStarted] = useState(false);
  const [connectingState, setConnectingState] =
    useState<StepState>('NotStarted');
  const [rewiringFailedReason, setRewiringFailedReason] =
    useState<RewiringFailedReason | null>(null);
  const [createConfigurationState, setCreateConfigurationState] =
    useState<StepState>('NotStarted');
  const client = usePulsoidClient();

  return (
    <div>
      <div>
        <p>
          You are trying to connect the Twitch extension installed on channel{' '}
          <strong>{channelName}</strong> to the current Pulsoid profile.
        </p>
        <p>
          However, the Twitch channel <strong>{channelName}</strong> is not
          connected to the current Pulsoid profile associated with the email.
        </p>
        <p>
          Do you wish to disconnect the Twitch channel{' '}
          <strong>{channelName}</strong> to the current Pulsoid Profile?
        </p>
      </div>
      <div>
        <div className={css.step}>
          <div>Connecting Twitch Account</div>
          <div>
            <div className={badgeStyle(connectingState)}>
              {ConnectingStepBadgeText[connectingState]}
            </div>
          </div>
          <div>
            {rewiringFailedReason == 'TwitchExtensionTokenInvalid' && (
              <>
                Connecting Twitch Channel failed due to extension token being
                invalid. To fix that:
                <ol>
                  <li>
                    go to{' '}
                    <a href="https://dashboard.twitch.tv/" target={'_blank'}>
                      twitch dashboard
                    </a>
                  </li>
                  <li>open 'Extensions' from the left side bar</li>
                  <li>open extension configuration</li>
                  <li>initiate connection process</li>
                </ol>
              </>
            )}
            {rewiringFailedReason == 'BroadcasterNotFound' && (
              <>
                Twitch channel with <i>{channelName}</i> name is not found.
                Please <a href="https://pulsoid.net/contact">contact us</a>.
              </>
            )}
          </div>
        </div>
        <div className={css.step}>
          <div>Connecting Twitch Extension</div>
          <div>
            <div className={badgeStyle(createConfigurationState)}>
              {CreateConfigurationBadgeText[createConfigurationState]}
            </div>
          </div>
          <div>
            {createConfigurationState == 'Failed' && (
              <>
                Connecting extension failed,
                <a href="https://pulsoid.net/contact">contact us</a>.
              </>
            )}
          </div>
        </div>
      </div>
      {connectingState == 'NotStarted' && (
        <ButtonV2
          disabled={connectingStarted}
          withClickLoader
          onClickAsync={async () => {
            try {
              setRewiringFailedReason(null);
              setConnectingStarted(true);
              setConnectingState('InProgress');
              try {
                await client.connectTwitchAccountByTwitchExtensionToken(
                  jwtToken
                );
                setConnectingState('Succeeded');
              } catch (error) {
                if (error instanceof JsonRpcException) {
                  let reason = error.data.reason as RewiringFailedReason;
                  if (reason == 'TwitchExtensionTokenInvalid') {
                    setRewiringFailedReason('TwitchExtensionTokenInvalid');
                    setConnectingState('Failed');
                  } else if (reason == 'BroadcasterNotFound') {
                    setRewiringFailedReason('BroadcasterNotFound');
                    setConnectingState('Failed');
                  } else {
                  }
                }
                return;
              }

              setCreateConfigurationState('InProgress');
              try {
                await client.createOrGetTwitchExtensionConfiguration();
                setCreateConfigurationState('Succeeded');
              } catch {
                setCreateConfigurationState('Failed');
              }
            } finally {
              setConnectingStarted(false);
            }
          }}
        >
          Connect Twitch Channel
        </ButtonV2>
      )}
      {connectingState == 'Succeeded' &&
        createConfigurationState == 'Succeeded' && (
          <div>
            <div>Twitch Extension is successfuly connected.</div>
            <a href="https://pulsoid.net/ui/twitch-extension-configuration">
              Go to Twitch Extension Configuration
            </a>
          </div>
        )}
    </div>
  );
};

const AlreadyConnected = () => {
  return (
    <div>
      <div>Twitch Extension is already connected.</div>
      <a href="https://pulsoid.net/ui/twitch-extension-configuration">
        Go to Twitch Extension Configuration
      </a>
    </div>
  );
};
